import { isClient, isServer } from "./global";
import config from "./config";
import { Store } from "redux";
import { ActionsUnion, State } from "./store/types";
import { decode } from "./utils/queryString";

type ReduxStore = Store<State, ActionsUnion> & {
  dispatch: {};
};

let store: ReduxStore | null = null;
export function setStore(s: ReduxStore) {
  store = s;
}

export function isEnabled() {
  return isClient() && Boolean((window as any).analytics);
}

if (isEnabled()) {
  window.analytics.load(config.SEGMENT_WRITE_KEY);
}

export const trackEvent: SegmentAnalytics.AnalyticsJS["track"] = (
  eventName: any,
  props?: any,
  options?: SegmentAnalytics.SegmentOpts,
  callback?: () => void
) => {
  if (isServer() || !isEnabled()) {
    return;
  }

  window.analytics.track(eventName, props, options, callback);

  internalTrack(eventName, props);
};

export const deprecatedTrack = (event: string, properties?: object) => {
  if (isServer() || !isEnabled()) {
    return;
  }

  let intercomId;
  if (store) {
    intercomId = store.getState().auth.intercomId;
  }

  (window.analytics as any).track(event, {
    ...properties,
    intercom_id: intercomId
  });

  internalTrack(event, properties);
};

export const identify = (userId: string, traits?: object) => {
  if (isServer() || !isEnabled()) {
    return;
  }

  window.analytics.identify(userId, traits);
};

export const deviceId = () => {
  if (isServer()) {
    return "";
  }

  try {
    if (!window.analytics) {
      return "";
    }

    let user;
    if (window.analytics.user) {
      user = window.analytics.user();
    }

    if (!user) {
      return "";
    }

    return user.anonymousId();
  } catch (err) {
    console.error("unable to get segment anonymous id", err);
    return "";
  }
};

export const reset = () => {
  if (isServer() || !isEnabled()) {
    return;
  }

  window.analytics.reset();
};

interface PageProperties {
  [key: string]: string | number;
}

export const page = (name?: string, properties?: PageProperties) => {
  if (isServer() || !isEnabled()) {
    return;
  }

  let intercomId;
  if (store) {
    intercomId = store.getState().auth.intercomId;
  }

  const props = { ...properties, intercom_id: intercomId };

  window.analytics.page(name, props);

  internalPageView(name, props);
};

function internalTrack(event: string, properties?: object) {
  if (isServer() || !isEnabled() || !config.INTERNAL_TRACKING_URL) {
    return;
  }

  window.analytics.ready(() => {
    const payload = makeSegmentPayload();
    payload.type = "track";
    payload.event = event;
    payload.properties = {
      ...properties
    };
    sendInternalTracking(payload);
  });
}

function internalPageView(name?: string, properties?: object) {
  if (isServer() || !isEnabled() || !config.INTERNAL_TRACKING_URL) {
    return;
  }

  window.analytics.ready(() => {
    const payload = makeSegmentPayload();
    payload.type = "page";
    payload.name = name;
    payload.properties = {
      ...payload.context.page,
      ...properties
    };
    sendInternalTracking(payload);
  });
}

function sendInternalTracking(payload: object) {
  // sendBeacon API is preferred as it's designed specifically for sending
  // analytics data - https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
  if (navigator.sendBeacon) {
    try {
      navigator.sendBeacon(
        config.INTERNAL_TRACKING_URL,
        JSON.stringify(payload)
      );
      return;
    } catch (err) {
      // Not doing anything in here, but will mean that we fall through to the block below and try fetch instead
    }
  }

  // Support for sendBeacon is very good (https://caniuse.com/?search=sendBeacon)
  // but just in case fall back to Fetch as we polyfill that if not supported
  window.fetch(config.INTERNAL_TRACKING_URL, {
    method: "POST",
    mode: "no-cors",
    body: JSON.stringify(payload)
  });
}

// This function creates an object that contains all fields common to all Segment
// messages.
export function makeSegmentPayload(): { [key: string]: any } {
  if (isServer() || !window.analytics) {
    return {};
  }

  let userId = null;
  let anonymousId = null;
  if (window.analytics.user) {
    const user = window.analytics.user();
    if (user) {
      userId = user.id();
      anonymousId = user.anonymousId();
    }
  }

  const ts = new Date().toISOString();
  const loc = window.location;

  // From Segment:
  // Dictionary of information about the campaign that resulted in the API call,
  // containing name, source, medium, term, content, and any other custom UTM parameter.
  // This maps directly to the common UTM campaign parameters.
  const query = decode(loc.search);
  let campaign: undefined | { [key: string]: string };
  for (const attr of ["name", "source", "medium", "term", "content"]) {
    const key = `utm_${attr}`;
    const values = query[key];
    if (values && values.length > 0) {
      campaign = campaign || {};
      campaign[attr] = values[0];
    }
  }

  return {
    timestamp: ts,
    context: {
      campaign,
      page: {
        path: loc.pathname,
        referrer: document.referrer,
        search: loc.search,
        title: document.title,
        url: loc.href
      },
      userAgent: navigator.userAgent,
      locale: navigator.language,
      library: {
        name: "analytics.js",
        version: (window as any).analytics.VERSION
      }
    },
    userId,
    anonymousId,
    sentAt: ts
  };
}
